import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useParentChild } from "@/contexts/ParentChildContext";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BookOpen, Sparkles, Trophy, ClipboardList, ArrowRight, TrendingUp, Users2, GraduationCap, Wand2, CheckSquare, Flame, Award, MessageSquare } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid, Area, AreaChart } from "recharts";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role;
  if (!role) return null;
  if (role === "student") return <StudentDashboard />;
  if (role === "teacher") return <TeacherDashboard />;
  if (role === "parent") return <ParentDashboard />;
  return <AdminDashboard />;
}

function KpiCard({ label, value, sub, icon: Icon, tint = "indigo", to, loading = false }) {
  const Wrap = to && !loading ? Link : "div";
  const props = to && !loading ? { to } : {};
  return (
    <Wrap {...props} className={`block p-5 rounded-2xl border border-border bg-card ambient-shadow ${to && !loading ? "hover:-translate-y-1 transition-transform duration-200 ease-out" : ""}`} aria-busy={loading || undefined}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
          {loading ? (
            <>
              <Skeleton className="h-8 w-16 mt-2" />
              <Skeleton className="h-3 w-20 mt-2" />
            </>
          ) : (
            <>
              <div className="font-display text-3xl font-bold mt-1.5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>{value}</div>
              {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
            </>
          )}
        </div>
        <div className={`w-10 h-10 rounded-xl bg-${tint}-500/15 grid place-items-center text-${tint}-500`} aria-hidden="true">
          <Icon size={18} />
        </div>
      </div>
    </Wrap>
  );
}

function StudentDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [gam, setGam] = useState(null);
  const [hw, setHw] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/dashboard").then((r) => setStats(r.data)),
      api.get("/gamification/me").then((r) => setGam(r.data)),
      api.get("/homework").then((r) => setHw(r.data.slice(0, 4))),
    ]).catch(() => toast.error("Veriler yüklenemedi"));
  }, []);

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4 animate-fade-up">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Merhaba {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-muted-foreground mt-1">Bugün öğrenmek için harika bir gün.</p>
        </div>
        <Link to="/ai/ogretmen">
          <Button className="rounded-full h-11 px-5" data-testid="student-quick-ai">
            <Sparkles size={16} className="mr-2" /> AI Öğretmen ile Konuş
          </Button>
        </Link>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-up" style={{animationDelay:"80ms"}}>
        <KpiCard loading={!stats} label="Ödevlerim" value={stats?.assigned_homework ?? stats?.toplam_odev ?? 0} sub={stats ? `${stats?.submitted_homework ?? 0} teslim edildi` : ""} icon={BookOpen} tint="cyan" to="/odevler" />
        <KpiCard loading={!stats} label="Not Ortalaması" value={stats?.ortalama_not ?? 0} sub="/ 100" icon={TrendingUp} tint="emerald" />
        <KpiCard loading={!stats} label="Sınavlar" value={stats?.assigned_exams ?? stats?.sinav_sayisi ?? 0} sub={stats ? `${stats?.submitted_exams ?? 0} tamamlandı` : ""} icon={ClipboardList} tint="indigo" to="/sinavlar" />
        <KpiCard loading={!gam} label="XP Puanı" value={gam?.xp ?? 0} sub={`Seviye ${gam?.level ?? 1}`} icon={Trophy} tint="orange" to="/basarilar" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 ambient-shadow animate-fade-up" style={{animationDelay:"160ms"}}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">Haftalık Aktivite</h3>
            <Badge variant="secondary" className="bg-cyan-500/10 text-cyan-600">Son 7 gün</Badge>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={stats?.haftalik || []}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="gun" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
              <Area dataKey="not" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 ambient-shadow animate-fade-up" style={{animationDelay:"200ms"}}>
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Flame size={18} className="text-orange-500" /> Seviye İlerlemesi</h3>
          <div className="text-center py-2">
            <div className="w-24 h-24 rounded-full mx-auto grid place-items-center bg-orange-500/10 mb-3">
              <span className="font-display text-3xl font-bold text-orange-600">{gam?.level ?? 1}</span>
            </div>
            <div className="text-sm text-muted-foreground mb-2">{gam?.xp ?? 0} / {gam?.next_level_xp ?? 100} XP</div>
            <Progress value={(gam?.progress ?? 0) * 100} className="h-2" />
          </div>
          <div className="mt-4 flex justify-center gap-2 flex-wrap">
            {gam?.badges?.slice(0, 4).map((b) => (
              <div key={b.id} className="w-10 h-10 rounded-lg bg-orange-500/10 grid place-items-center" title={b.name}>
                <Award size={16} className="text-orange-500" />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6 ambient-shadow animate-fade-up" style={{animationDelay:"240ms"}}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold">Yaklaşan Ödevler</h3>
          <Link to="/odevler"><Button variant="ghost" size="sm" data-testid="view-all-homework">Tümü <ArrowRight size={14} className="ml-1" /></Button></Link>
        </div>
        <div className="space-y-2">
          {hw.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">Ödev yok</div>}
          {hw.map((h) => <HomeworkRow key={h.id} hw={h} />)}
        </div>
      </Card>
    </div>
  );
}

function HomeworkRow({ hw }) {
  const due = new Date(hw.due_date);
  const days = Math.ceil((due - new Date()) / (86400 * 1000));
  const status = hw.submission?.status;
  return (
    <Link to={`/odevler/${hw.id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-foreground/[0.04] border border-transparent hover:border-border transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-indigo-500/10 text-indigo-600 grid place-items-center shrink-0">
          <BookOpen size={16} />
        </div>
        <div className="min-w-0">
          <div className="font-medium truncate">{hw.title}</div>
          <div className="text-xs text-muted-foreground">{hw.subject} · {days > 0 ? `${days} gün kaldı` : "Süresi geçti"}</div>
        </div>
      </div>
      {status === "graded" ? <Badge className="bg-emerald-500/15 text-emerald-700">Değerlendirildi</Badge> :
        status === "submitted" ? <Badge className="bg-indigo-500/15 text-indigo-700">Teslim Edildi</Badge> :
        days < 0 ? <Badge className="bg-red-500/15 text-red-700">Süresi Geçti</Badge> :
        <Badge variant="outline">Bekliyor</Badge>}
    </Link>
  );
}

function TeacherDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [hw, setHw] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/analytics/dashboard").then((r) => setStats(r.data)),
      api.get("/homework").then((r) => setHw(r.data.slice(0, 5))),
    ]);
  }, []);
  return (
    <div className="space-y-8">
      <header className="flex flex-wrap justify-between items-end gap-4 animate-fade-up">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Merhaba {user?.name?.split(" ")[0]}</h1>
          <p className="text-muted-foreground mt-1">Bugünün sınıfına hoş geldiniz.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/odevler/yeni"><Button className="rounded-full h-11 px-5" data-testid="teacher-new-hw"><BookOpen size={16} className="mr-2" /> Yeni Ödev</Button></Link>
          <Link to="/ai/sinav-uret"><Button variant="outline" className="rounded-full h-11 px-5" data-testid="teacher-ai-exam"><Wand2 size={16} className="mr-2" /> AI Sınav</Button></Link>
        </div>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-up" style={{animationDelay:"80ms"}}>
        <KpiCard loading={!stats} label="Toplam Ödev" value={stats?.toplam_odev ?? 0} icon={BookOpen} tint="indigo" to="/odevler" />
        <KpiCard loading={!stats} label="Teslim Edilen" value={stats?.teslim_edilen ?? 0} icon={CheckSquare} tint="emerald" />
        <KpiCard loading={!stats} label="Sınav Sayısı" value={stats?.toplam_sinav ?? 0} icon={ClipboardList} tint="cyan" to="/sinavlar" />
        <KpiCard loading={!stats} label="Ortalama Not" value={stats?.ortalama_not ?? 0} sub="/100" icon={TrendingUp} tint="orange" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6 ambient-shadow animate-fade-up">
          <h3 className="font-display text-lg font-semibold mb-4">Haftalık Teslim Grafiği</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats?.haftalik || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="gun" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
              <Bar dataKey="odev" fill="hsl(var(--chart-1))" radius={[6,6,0,0]} />
              <Bar dataKey="teslim" fill="hsl(var(--chart-2))" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 ambient-shadow animate-fade-up">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">Son Ödevler</h3>
            <Link to="/odevler"><Button variant="ghost" size="sm">Tümü</Button></Link>
          </div>
          <div className="space-y-2">
            {hw.map((h) => (
              <Link key={h.id} to={`/odevler/${h.id}`} className="flex justify-between items-center p-3 rounded-xl hover:bg-foreground/[0.04]">
                <div>
                  <div className="font-medium">{h.title}</div>
                  <div className="text-xs text-muted-foreground">{h.subject}</div>
                </div>
                <Badge variant="outline">{new Date(h.due_date).toLocaleDateString("tr-TR")}</Badge>
              </Link>
            ))}
            {hw.length === 0 && <div className="text-center text-sm text-muted-foreground py-6">Henüz ödev yok</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function ParentDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const { activeChildId, setActiveChildId, hasMultiple } = useParentChild();
  useEffect(() => { api.get("/analytics/dashboard").then((r) => setStats(r.data)); }, []);
  const allChildren = stats?.cocuklar || [];
  const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const visibleChildren = activeChildId === "all" ? allChildren : allChildren.filter((c) => c.id === activeChildId);
  const totalHw = allChildren.reduce((s, c) => s + (c.assigned_homework ?? c.toplam_odev ?? 0), 0);
  const totalSubmitted = allChildren.reduce((s, c) => s + (c.submitted_homework ?? 0), 0);
  return (
    <div className="space-y-8">
      <header className="animate-fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Merhaba {user?.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground mt-1">
          {hasMultiple ? `${allChildren.length} çocuğunuzun gelişimini takip edin.` : "Çocuğunuzun gelişimini takip edin."}
        </p>
      </header>

      {allChildren.length === 0 && (
        <Card className="p-8 text-center">
          <Users2 className="mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">Henüz hesabınıza bağlı bir öğrenci yok.</p>
        </Card>
      )}

      {hasMultiple && (
        <div className="animate-fade-up space-y-2" data-testid="parent-child-selector">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Çocuk seçimi</div>
          <div className="flex items-center gap-2 flex-wrap p-1.5 rounded-2xl border border-border bg-card">
            <button
              onClick={() => setActiveChildId("all")}
              data-testid="child-tab-all"
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-colors ${activeChildId === "all" ? "bg-indigo-500 text-white shadow-sm" : "text-muted-foreground hover:bg-muted"}`}
            >
              <Users2 size={14} /> Tümü <span className="text-[10px] opacity-75">({allChildren.length})</span>
            </button>
            {allChildren.map((c) => {
              const active = activeChildId === c.id;
              const submitRate = (c.assigned_homework ?? c.toplam_odev ?? 0) > 0
                ? Math.round(((c.submitted_homework ?? 0) / (c.assigned_homework ?? c.toplam_odev ?? 1)) * 100)
                : null;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveChildId(c.id)}
                  data-testid={`child-tab-${c.id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${active ? "bg-indigo-500 text-white shadow-sm" : "text-foreground hover:bg-muted"}`}
                  aria-pressed={active}
                >
                  <span className={`w-7 h-7 rounded-lg grid place-items-center text-[11px] font-bold ${active ? "bg-white/20 text-white" : "bg-orange-500/15 text-orange-600"}`}>
                    {initials(c.name)}
                  </span>
                  <span className="max-w-[110px] truncate">{c.name?.split(" ")[0]}</span>
                  {submitRate !== null && submitRate < 50 && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${active ? "bg-white/20 text-white" : "bg-amber-500/15 text-amber-700"}`} title="Ödev teslim oranı düşük">
                      %{submitRate}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {activeChildId === "all" && (
            <div className="text-xs text-muted-foreground flex items-center gap-3 pt-1" data-testid="parent-multi-summary">
              <span>Toplam ödev: <strong className="text-foreground">{totalHw}</strong></span>
              <span className="text-border">•</span>
              <span>Teslim edilen: <strong className="text-foreground">{totalSubmitted}</strong></span>
              <span className="text-border">•</span>
              <span>Genel oran: <strong className={totalHw > 0 && totalSubmitted / totalHw < 0.5 ? "text-amber-600" : "text-emerald-600"}>{totalHw > 0 ? Math.min(Math.round((totalSubmitted / totalHw) * 100), 100) : 0}%</strong></span>
            </div>
          )}
        </div>
      )}

      <div className={`grid gap-4 animate-fade-up ${visibleChildren.length === 1 ? "md:grid-cols-1 max-w-2xl" : "md:grid-cols-2"}`}>
        {visibleChildren.map((c) => (
          <Card key={c.id} className="p-6 ambient-shadow hover:-translate-y-1 transition-transform duration-200 ease-out" data-testid={`parent-child-card-${c.id}`}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-orange-500/15 grid place-items-center">
                <GraduationCap className="text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl font-bold flex items-center gap-2">
                  {c.name}
                  {allChildren.length > 1 && activeChildId === c.id && <Badge variant="outline" className="text-[9px] border-indigo-500/50 text-indigo-600">SEÇİLİ</Badge>}
                </div>
                <div className="text-sm text-muted-foreground">{c.grade}. Sınıf · Seviye {c.level}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="text-center p-3 rounded-xl bg-secondary" title={`${c.submitted_homework ?? 0} tanesi teslim edildi`}>
                <div className="font-display text-2xl font-bold" data-testid={`parent-child-hw-${c.id}`}>{c.assigned_homework ?? c.toplam_odev ?? 0}</div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">Ödev</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-secondary">
                <div className="font-display text-2xl font-bold">{c.ortalama}</div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">Ortalama</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-secondary">
                <div className="font-display text-2xl font-bold">{c.xp}</div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">XP</div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Link to={`/odevler?ogrenci=${c.id}`} className="flex-1"><Button variant="outline" className="w-full">Ödevleri Gör</Button></Link>
              <Link to="/ai/koc" className="flex-1"><Button className="w-full"><Wand2 size={14} className="mr-2" />AI Koç</Button></Link>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/analytics/dashboard").then((r) => setStats(r.data)); }, []);
  return (
    <div className="space-y-8">
      <header className="animate-fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Yönetici Paneli</h1>
        <p className="text-muted-foreground mt-1">Sistem geneli özet ve KPI&apos;lar.</p>
      </header>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 animate-fade-up">
        <KpiCard loading={!stats} label="Toplam Kullanıcı" value={stats?.toplam_kullanici ?? 0} icon={Users2} tint="emerald" />
        <KpiCard loading={!stats} label="Öğrenci" value={stats?.toplam_ogrenci ?? 0} icon={GraduationCap} tint="cyan" />
        <KpiCard loading={!stats} label="Öğretmen" value={stats?.toplam_ogretmen ?? 0} icon={Users2} tint="indigo" />
        <KpiCard loading={!stats} label="Ödev" value={stats?.toplam_odev ?? 0} icon={BookOpen} tint="orange" to="/odevler" />
        <KpiCard loading={!stats} label="Sınav" value={stats?.toplam_sinav ?? 0} icon={ClipboardList} tint="cyan" to="/sinavlar" />
      </div>
      <Card className="p-6 ambient-shadow animate-fade-up">
        <h3 className="font-display text-lg font-semibold mb-4">Aylık Kullanıcı Büyümesi</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={stats?.aylik || []}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis dataKey="ay" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
            <Line dataKey="kullanici" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={{ r: 4 }} />
            <Line dataKey="aktif" stroke="hsl(var(--chart-2))" strokeWidth={2.5} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
